import React, { FC } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tabs,
  Tab,
  Paper,
  Stack,
  LinearProgress,
  useTheme,
} from '@mui/material';
import {
  Download,
  Email,
  Print,
  FilterList,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  School,
  AccountBalance,
  Receipt,
  Assessment,
  PieChart,
  BarChart,
} from '@mui/icons-material';

import {
  useFinancialReports,
  FinancialReportsProvider,
} from './financialReports.context';
import {
  TabPanelProps,
  SummaryCardProps,
  ReportTab,
  ReportPeriod,
  Department,
} from './financialReports.interfaces';
import {
  FeeCollectionReportGrid,
  ExpenseReportGrid,
  ScholarshipReportGrid,
  BudgetReportGrid,
} from './subviews/financialReports.tabPanels';
import { useTranslation } from 'src/hooks/useTranslationHook';

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`financial-tabpanel-${index}`}
      aria-labelledby={`financial-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }} data-testid={`financial-tabpanel-${index}`}>
          {children}
        </Box>
      )}
    </Box>
  );
}

const FinancialReportsContent: FC = () => {
  const { translateText } = useTranslation();
  const theme = useTheme();
  const {
    state,
    setSelectedTab,
    setAcademicYear,
    setReportPeriod,
    setDepartment,
    setSearchQuery,
    formatCurrency,
    exportReport,
    sendReportEmail,
    printReport,
  } = useFinancialReports();

  const { selectedTab, filters, summaryData, complianceReports, auditReports } =
    state;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue as ReportTab);
  };

  const handleExport = async () => {
    const reportTypes = [
      translateText('finance.reports.reportTypes.feeCollection'),
      translateText('finance.reports.reportTypes.expenses'),
      translateText('finance.reports.reportTypes.scholarships'),
      translateText('finance.reports.reportTypes.budgetVsActual'),
      translateText('finance.reports.reportTypes.compliance'),
      translateText('finance.reports.reportTypes.audit'),
    ];
    await exportReport(reportTypes[selectedTab], 'PDF');
  };

  const handleEmail = async () => {
    const reportTypes = [
      translateText('finance.reports.reportTypes.feeCollection'),
      translateText('finance.reports.reportTypes.expenses'),
      translateText('finance.reports.reportTypes.scholarships'),
      translateText('finance.reports.reportTypes.budgetVsActual'),
      translateText('finance.reports.reportTypes.compliance'),
      translateText('finance.reports.reportTypes.audit'),
    ];
    await sendReportEmail(reportTypes[selectedTab], ['admin@school.com']);
  };

  const handlePrint = () => {
    const reportTypes = [
      translateText('finance.reports.reportTypes.feeCollection'),
      translateText('finance.reports.reportTypes.expenses'),
      translateText('finance.reports.reportTypes.scholarships'),
      translateText('finance.reports.reportTypes.budgetVsActual'),
      translateText('finance.reports.reportTypes.compliance'),
      translateText('finance.reports.reportTypes.audit'),
    ];
    printReport(reportTypes[selectedTab]);
  };

  const SummaryCard: FC<SummaryCardProps> = ({
    title,
    value,
    subtitle,
    icon,
    trend,
  }) => {
    const getCardColor = () => {
      // Prioritize trend-based colors when available
      if (trend !== undefined && trend !== null) {
        return trend > 0 ? 'success' : 'error';
      }
      // Fall back to icon color
      const iconColor = (icon as any)?.props?.color;
      if (iconColor === 'success') return 'success';
      if (iconColor === 'warning') return 'warning';
      if (iconColor === 'error') return 'error';
      if (iconColor === 'primary') return 'primary';
      return 'primary';
    };

    const cardColor = getCardColor();
    const colorPalette = theme.palette[cardColor];

    return (
      <Card
        elevation={0}
        sx={{
          background: `linear-gradient(135deg, ${colorPalette.light}15 0%, ${colorPalette.light}05 100%)`,
          border: `1px solid ${colorPalette.light}40`,
          borderRadius: 2,
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 12px ${colorPalette.light}30`,
          },
          height: '100%',
        }}
        data-testid={`financial-summary-card-${cardColor}`}
      >
        <CardContent>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between">
            <Box flex={1}>
              <Typography
                color="textSecondary"
                gutterBottom
                variant="caption"
                fontWeight={500}
              >
                {title}
              </Typography>
              <Typography
                variant="h6"
                component="div"
                fontWeight={600}
                color={colorPalette.main}
                mb={subtitle ? 1 : 0}
              >
                {value}
              </Typography>
              {subtitle && (
                <Typography variant="body2" color="textSecondary" fontSize="0.875rem">
                  {subtitle}
                </Typography>
              )}
            </Box>
            <Box display="flex" flexDirection="column" alignItems="center" ml={2}>
              <Box
                sx={{
                  '& .MuiSvgIcon-root': {
                    fontSize: 32,
                    color: colorPalette.main,
                  },
                }}
              >
                {icon}
              </Box>
              {trend !== undefined && (
                <Box display="flex" alignItems="center" mt={1}>
                  {trend > 0 ? (
                    <TrendingUp color="success" fontSize="small" />
                  ) : (
                    <TrendingDown color="error" fontSize="small" />
                  )}
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color={trend > 0 ? 'success.main' : 'error.main'}
                    ml={0.5}
                  >
                    {Math.abs(trend)}%
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }} data-testid="financial-reports-container">
      {/* Header Section */}
      <Box
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        gap={{ xs: 2, md: 0 }}
        mb={3}
        data-testid="financial-reports-header"
      >
        <Typography
          variant="h4"
          fontWeight="bold"
          sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}
          data-testid="financial-reports-title"
        >
          {translateText('finance.reports.title')}
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 2 }}
          width={{ xs: '100%', md: 'auto' }}
          data-testid="financial-reports-actions"
        >
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            size="small"
            data-testid="financial-reports-filter-button"
            sx={{
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
          >
            {translateText('finance.reports.filters')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            size="small"
            onClick={handleExport}
            data-testid="financial-reports-export-button"
            sx={{
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
          >
            {translateText('finance.reports.export')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Email />}
            size="small"
            onClick={handleEmail}
            data-testid="financial-reports-email-button"
            sx={{
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
          >
            {translateText('finance.reports.email')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Print />}
            size="small"
            onClick={handlePrint}
            data-testid="financial-reports-print-button"
            sx={{
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
          >
            {translateText('finance.reports.print')}
          </Button>
        </Stack>
      </Box>

      {/* Filters Section */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: 3,
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
        data-testid="financial-reports-filters"
      >
        <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>
                {translateText('finance.reports.academicYear')}
              </InputLabel>
              <Select
                value={filters.academicYear}
                label={translateText('finance.reports.academicYear')}
                onChange={(e) => setAcademicYear(e.target.value)}
              >
                <MenuItem value="2024-25">2024-25</MenuItem>
                <MenuItem value="2023-24">2023-24</MenuItem>
                <MenuItem value="2022-23">2022-23</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{translateText('finance.reports.period')}</InputLabel>
              <Select
                value={filters.reportPeriod}
                label={translateText('finance.reports.period')}
                onChange={(e) =>
                  setReportPeriod(e.target.value as ReportPeriod)
                }
              >
                <MenuItem value={ReportPeriod.MONTHLY}>
                  {translateText('finance.reports.periods.monthly')}
                </MenuItem>
                <MenuItem value={ReportPeriod.QUARTERLY}>
                  {translateText('finance.reports.periods.quarterly')}
                </MenuItem>
                <MenuItem value={ReportPeriod.YEARLY}>
                  {translateText('finance.reports.periods.yearly')}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>
                {translateText('finance.reports.department')}
              </InputLabel>
              <Select
                value={filters.department}
                label={translateText('finance.reports.department')}
                onChange={(e) => setDepartment(e.target.value as Department)}
              >
                <MenuItem value={Department.ALL}>
                  {translateText('finance.reports.departments.all')}
                </MenuItem>
                <MenuItem value={Department.ACADEMICS}>
                  {translateText('finance.reports.departments.academics')}
                </MenuItem>
                <MenuItem value={Department.TRANSPORT}>
                  {translateText('finance.reports.departments.transport')}
                </MenuItem>
                <MenuItem value={Department.HOSTEL}>
                  {translateText('finance.reports.departments.hostel')}
                </MenuItem>
                <MenuItem value={Department.ADMIN}>
                  {translateText('finance.reports.departments.admin')}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label={translateText('finance.reports.search')}
              placeholder={translateText('finance.reports.searchPlaceholder')}
              value={filters.searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Dashboard */}
      <Typography
        variant="h5"
        fontWeight="bold"
        gutterBottom
        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
        data-testid="financial-reports-summary-title"
      >
        {translateText('finance.reports.summary.title')}
      </Typography>
      <Grid container spacing={{ xs: 2, sm: 2, md: 3, lg: 4 }} mb={4}>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <SummaryCard
            title={translateText('finance.reports.summary.totalIncomeYTD')}
            value={formatCurrency(summaryData.totalIncomeYTD)}
            subtitle={`${translateText('finance.reports.summary.monthly')}: ${formatCurrency(summaryData.totalIncomeMonthly)}`}
            icon={<AttachMoney color="success" />}
            trend={8.5}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <SummaryCard
            title={translateText('finance.reports.summary.totalExpenditureYTD')}
            value={formatCurrency(summaryData.totalExpenditureYTD)}
            subtitle={`${translateText('finance.reports.summary.monthly')}: ${formatCurrency(summaryData.totalExpenditureMonthly)}`}
            icon={<Receipt color="warning" />}
            trend={-2.3}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <SummaryCard
            title={translateText('finance.reports.summary.netSurplus')}
            value={formatCurrency(summaryData.netSurplus)}
            subtitle={translateText('finance.reports.summary.currentYear')}
            icon={<TrendingUp color="success" />}
            trend={15.2}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <SummaryCard
            title={translateText('finance.reports.summary.outstandingFees')}
            value={`${summaryData.outstandingFeesCount} ${translateText('finance.reports.summary.students')}`}
            subtitle={formatCurrency(summaryData.outstandingFeesAmount)}
            icon={<School color="error" />}
          />
        </Grid>
      </Grid>

      <Grid container spacing={{ xs: 2, sm: 2, md: 3, lg: 4 }} mb={4}>
        <Grid item xs={12} md={6}>
          <SummaryCard
            title={translateText(
              'finance.reports.summary.scholarshipsDisbursed'
            )}
            value={formatCurrency(summaryData.scholarshipsDisbursed)}
            subtitle={translateText('finance.reports.summary.thisAcademicYear')}
            icon={<AccountBalance color="primary" />}
            trend={12.1}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.primary.light}05 100%)`,
              border: `1px solid ${theme.palette.primary.light}40`,
              borderRadius: 2,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 12px ${theme.palette.primary.light}30`,
              },
              height: '100%',
            }}
            data-testid="financial-reports-budget-utilization-card"
          >
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                fontWeight={600}
                color={theme.palette.primary.main}
              >
                {translateText('finance.reports.summary.budgetUtilization')}
              </Typography>
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" fontWeight={500}>
                    {translateText('finance.reports.summary.operatingExpenses')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="primary">
                    75%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={75}
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    backgroundColor: theme.palette.grey[200],
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 1,
                    },
                  }}
                />
              </Box>
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" fontWeight={500}>
                    {translateText('finance.reports.summary.infrastructure')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="primary">
                    45%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={45}
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    backgroundColor: theme.palette.grey[200],
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 1,
                    },
                  }}
                />
              </Box>
              <Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" fontWeight={500}>
                    {translateText('finance.reports.summary.academicPrograms')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="primary">
                    82%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={82}
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    backgroundColor: theme.palette.grey[200],
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 1,
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Report Types Tabs */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
        data-testid="financial-reports-tabs-container"
      >
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: theme.palette.grey[50],
            '& .MuiTab-root': {
              minWidth: { xs: 100, sm: 160 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              padding: { xs: '6px 4px', sm: '12px 16px' },
              fontWeight: 500,
              '&.Mui-selected': {
                fontWeight: 600,
              },
            },
          }}
          data-testid="financial-reports-tabs"
        >
          <Tab
            label={translateText('finance.reports.tabs.feeCollection')}
            icon={<Receipt />}
          />
          <Tab
            label={translateText('finance.reports.tabs.expenses')}
            icon={<BarChart />}
          />
          <Tab
            label={translateText('finance.reports.tabs.scholarships')}
            icon={<School />}
          />
          <Tab
            label={translateText('finance.reports.tabs.budgetVsActual')}
            icon={<Assessment />}
          />
          <Tab
            label={translateText('finance.reports.tabs.compliance')}
            icon={<AccountBalance />}
          />
          <Tab
            label={translateText('finance.reports.tabs.auditReports')}
            icon={<PieChart />}
          />
        </Tabs>

        {/* Fee Collection Reports */}
        <TabPanel value={selectedTab} index={0}>
          <FeeCollectionReportGrid />
        </TabPanel>

        {/* Expense Reports */}
        <TabPanel value={selectedTab} index={1}>
          <ExpenseReportGrid />
        </TabPanel>

        {/* Scholarship & Grants */}
        <TabPanel value={selectedTab} index={2}>
          <ScholarshipReportGrid />
        </TabPanel>

        {/* Budget vs Actual */}
        <TabPanel value={selectedTab} index={3}>
          <BudgetReportGrid />
        </TabPanel>

        {/* Compliance Reports */}
        <TabPanel value={selectedTab} index={4}>
          <Grid container spacing={{ xs: 2, sm: 3 }} data-testid="compliance-reports-grid">
            {complianceReports.map((report) => (
              <Grid key={report.id} item xs={12} sm={6} md={4}>
                <Card
                  variant="outlined"
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.info.light}10 0%, ${theme.palette.info.light}05 100%)`,
                    border: `1px solid ${theme.palette.info.light}40`,
                    borderRadius: 2,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    height: '100%',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${theme.palette.info.light}30`,
                    },
                  }}
                  data-testid={`compliance-report-card-${report.id}`}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      {report.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                      sx={{ minHeight: '40px' }}
                    >
                      {report.description}
                    </Typography>
                    <Stack spacing={{ xs: 1, sm: 2 }} mt={{ xs: 1, sm: 2 }}>
                      <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<Download />}
                        size="small"
                        data-testid={`compliance-download-button-${report.id}`}
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          borderColor: theme.palette.info.main,
                          color: theme.palette.info.main,
                          '&:hover': {
                            borderColor: theme.palette.info.dark,
                            backgroundColor: `${theme.palette.info.main}10`,
                          },
                        }}
                      >
                        {translateText(
                          'finance.reports.compliance.downloadReport',
                          { reportType: report.reportType }
                        )}
                      </Button>
                      <Typography variant="caption" color="textSecondary">
                        {translateText(
                          'finance.reports.compliance.lastUpdated'
                        )}
                        : {report.lastUpdated}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Audit Reports */}
        <TabPanel value={selectedTab} index={5}>
          <Grid container spacing={{ xs: 2, sm: 3 }} data-testid="audit-reports-grid">
            {auditReports.map((report) => (
              <Grid key={report.id} item xs={12} sm={6} md={4}>
                <Card
                  variant="outlined"
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.secondary.light}10 0%, ${theme.palette.secondary.light}05 100%)`,
                    border: `1px solid ${theme.palette.secondary.light}40`,
                    borderRadius: 2,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    height: '100%',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${theme.palette.secondary.light}30`,
                    },
                  }}
                  data-testid={`audit-report-card-${report.id}`}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      {report.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                      sx={{ minHeight: '40px' }}
                    >
                      {report.description}
                    </Typography>
                    <Stack spacing={{ xs: 1, sm: 2 }} mt={{ xs: 1, sm: 2 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<Download />}
                        size="small"
                        disabled={!report.isAvailable}
                        data-testid={`audit-generate-button-${report.id}`}
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          fontWeight: 600,
                        }}
                      >
                        {translateText('finance.reports.audit.generateReport')}
                      </Button>
                      <Typography variant="body2" color="primary" fontWeight={500}>
                        {translateText('finance.reports.audit.availableFor')}:{' '}
                        {report.academicYear}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>
      </Paper>
    </Box>
  );
};

// Main wrapper component with provider
const FinancialReports: FC = () => {
  return (
    <FinancialReportsProvider>
      <FinancialReportsContent />
    </FinancialReportsProvider>
  );
};

export default FinancialReports;
